import React from "react";
import { toast } from "@components/ui/use-toast";
import {
  CircleCheckBig,
  Copy,
  Database,
  FileText,
  Gauge,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ArrowDown,
  ArrowUp,
} from "lucide-react";

type props = {
  response: any;
  renderJson: (
    data: any,
    level?: number,
    pathPrefix?: string,
  ) => React.ReactNode;
};

const QueryBuilderResponse: React.FC<props> = ({ response, renderJson }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [showScrollButton, setShowScrollButton] = React.useState(false);
  const [isAtBottom, setIsAtBottom] = React.useState(false);
  const responseContainerRef = React.useRef<HTMLDivElement>(null);

  // Check if content is scrollable and update scroll button visibility
  React.useEffect(() => {
    const container = responseContainerRef.current;
    if (!container || !isExpanded) {
      setShowScrollButton(false);
      return;
    }

    const checkScrollable = () => {
      const isScrollable = container.scrollHeight > container.clientHeight;
      setShowScrollButton(isScrollable);

      // Check initial position
      if (isScrollable) {
        const scrolledToBottom =
          container.scrollHeight - container.scrollTop <=
          container.clientHeight + 5;
        setIsAtBottom(scrolledToBottom);
      }
    };

    checkScrollable();
    // Recheck when content changes
    const observer = new ResizeObserver(checkScrollable);
    observer.observe(container);

    return () => observer.disconnect();
  }, [response, isExpanded]);

  // Handle scroll position
  const handleScroll = () => {
    const container = responseContainerRef.current;
    if (!container) return;

    const scrolledToBottom =
      container.scrollHeight - container.scrollTop <=
      container.clientHeight + 5;
    setIsAtBottom(scrolledToBottom);
  };

  // Prevent scroll fight with outer container: consume wheel while
  // the response panel can still scroll in the wheel direction.
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!isExpanded) return;

    const container = responseContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollingDown = e.deltaY > 0;
    const isScrollingUp = e.deltaY < 0;
    const canScrollDown = scrollTop + clientHeight < scrollHeight - 1;
    const canScrollUp = scrollTop > 0;

    if ((isScrollingDown && canScrollDown) || (isScrollingUp && canScrollUp)) {
      e.stopPropagation();
    }
  };

  // Scroll to top or bottom
  const handleScrollToggle = () => {
    const container = responseContainerRef.current;
    if (!container) return;

    if (isAtBottom) {
      // Scroll to top
      container.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Scroll to bottom
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  };

  return (
    <React.Fragment>
      {response && response.metrics && (
        <div className="mt-3 text-xs text-muted-foreground flex items-end gap-6 justify-end mr-2 font-semibold">
          <p className="flex items-center gap-1 text-muted-foreground">
            <CircleCheckBig size={16} className="text-primaryGreen" />
            {response.metrics.status} OK
          </p>

          <p className="flex items-center gap-1 text-muted-foreground">
            <RotateCcw size={14} className="text-primaryAmber" />
            {response.metrics.durationMs} ms
          </p>

          <p className="flex items-center gap-1 text-muted-foreground">
            <Database size={14} className="text-primary" />
            {response.metrics.sizeKB} KB
          </p>

          <p className="flex items-center gap-1 text-muted-foreground">
            <Gauge size={16} className="text-primaryBlue" />
            {response.metrics.speedKBps} KB/s
          </p>

          <p className="flex items-center gap-1 text-muted-foreground">
            <FileText size={14} className="text-foreground" />
            {response.metrics.responseType}
          </p>
        </div>
      )}

      {/* Response */}
      {response && (
        <div className="mt-2 border p-4 text-sm bg-background rounded-lg relative">
          <div className="absolute top-3 right-3 flex items-center gap-4">
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  JSON.stringify(response.data, null, 2),
                );
                toast({
                  variant: "info",
                  title: "Copied!",
                  description: "Response copied to clipboard.",
                  duration: 2000,
                });
              }}
              className="text-muted-foreground hover:text-primary transition flex items-center gap-1"
              aria-label="Copy response"
            >
              <Copy size={15} /> <span>Copy response</span>
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-muted-foreground hover:text-primary transition flex items-center gap-1"
              aria-label={isExpanded ? "Collapse response" : "Expand response"}
            >
              {isExpanded ? (
                <>
                  <ChevronUp size={18} /> <span>Collapse</span>
                </>
              ) : (
                <>
                  <ChevronDown size={18} /> <span>Expand</span>
                </>
              )}
            </button>
          </div>

          <div
            ref={responseContainerRef}
            onScroll={handleScroll}
            onWheel={handleWheel}
            className={`overflow-x-auto max-w-full mt-4 ${!isExpanded ? "max-h-[75px] overflow-y-hidden" : "max-h-[500px] overflow-y-auto"}`}
          >
            <pre className="whitespace-pre-wrap break-all text-sm">
              {renderJson(response.data)}
            </pre>

            {/* Scroll Button */}
            {showScrollButton && isExpanded && (
              <div className="sticky bottom-2 flex justify-end pr-2 pointer-events-none">
                <button
                  onClick={handleScrollToggle}
                  className="
                    p-3 rounded-full z-10
                    text-white
                    bg-gradient-to-br from-primary/80 via-primary/60 to-primary/40
                    backdrop-blur-sm
                    border border-white/20
                    shadow-[0_10px_30px_rgba(0,0,0,0.3)]
                    hover:scale-105
                    hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)]
                    transition-all duration-100
                    pointer-events-auto
                  "
                  aria-label={isAtBottom ? "Scroll to top" : "Scroll to bottom"}
                  title={isAtBottom ? "Scroll to top" : "Scroll to bottom"}
                >
                  <span className="text-foreground">
                    {isAtBottom ? (
                      <ArrowUp size={18} />
                    ) : (
                      <ArrowDown size={18} />
                    )}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default QueryBuilderResponse;
